import { PublicClientApplication } from '@azure/msal-browser';
import { IImageProps } from '@fluentui/react';
import { Client } from '@microsoft/microsoft-graph-client';
import { IPImage } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPLink } from 'baseComponents/Link/IPLink';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import { isAbortOrCancelError } from 'components/Notification/ErrorMessageHelper';
import { globalCloudSetupURL, globalDesktopHeight, globalDesktopSize } from 'globalConstants';
import {
  globalCustomerId,
  globalOrgUnitId,
  setGlobalCustomerId,
  setGlobalDefOrgLang,
  setGlobalOrgUnitId,
  setGlobalUserLang,
} from 'globalState';
import { applyThemeCssVars } from 'globalStyles';
import { CopilotBridge, CopilotBridgeState } from 'models/copilot/copilotBridge';
import CurrentUser from 'models/currentuser';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { getIdentityProviderName, IdentityProviderType } from 'models/identityProvider';
import { SelectedSitesStep, SelectedSitesSteps } from 'models/SelectedSitesSteps';
import { APISelectedSites, APPSelectedSites, AzureADPhotos, DefLanguageCode, SVCSelectedSites } from 'models/setting';
import Tenant from 'models/tenant';
import UserLanguage from 'models/userLanguage';
import React, { ReactNode } from 'react';
import { toast } from 'react-toastify';
import { apiGetTenantsForUser } from 'services/Api/tenantService';
import {
  apiClientId,
  apiRequest,
  appClientId,
  GraphScopeId,
  graphSharepointLibraryRequest,
  graphSharepointManageRequest,
  graphSharepointPagesRequest,
  graphSharepointSelectedRequest,
  msalConfigCommon,
  svcClientId,
} from 'services/Auth/authConfig';
import { AuthErrorCodes } from 'services/Auth/authErrors';
import { IAuthProvider } from 'services/Auth/authProvider';
import AuthService from 'services/Auth/authService';
import { MsalAuthProvider } from 'services/Auth/msalAuthProvider';
import Config from 'services/Config/configService';
import { resetHubSpotChat } from 'services/HubSpot/hubSpotChat';
import { i18nBase } from 'services/Localization/i18n';
import logger from 'services/Logging/logService';
import { OidcAuthProvider } from 'services/Oidc/oidcAuthProvider';
import { isGoogleLoginEnabled, isOidcLoginEnabled } from 'services/Oidc/oidcConfig';
import { initSupademo, updateSupademoUser } from 'services/SupaDemo/supaDemo';
import AppError, { normalizeError } from 'utils/appError';
import { isValidGuid, normalizeGuid } from 'utils/guid';
import { getLocalStorageData, LocalStorageKeys, removeLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import {
  getSessionStorageData,
  removeSessionStorageData,
  SessionStorageKeys,
  setSessionStorageData,
} from 'utils/sessionStorage';
import { SubscriptionTypes } from 'utils/subscription';
import AppContext, { IAppContext } from './AppContext';

//
// Types
//
export type AuthStateUpdate = (
  isAuthenticated?: boolean | undefined,
  user?: CurrentUser | undefined,
  isAuthInProgress?: boolean | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any | undefined,
) => void;

export type SetGlobalDataCache = (globalDataCache: GlobalDataCache) => void;

export type IGraphInterface = {
  accessToken: string;
  client: Client;
};

//what LocalStorageKeys.LastSeparateDirectoryOrgUnit holds: the org unit and the directory it signs in with
type SeparateDirectoryOrgUnitHint = {
  orgUnitId: string;
  identityProviderType: IdentityProviderType;
  providerTenantId: string;
};

//Main logo
export const imagePropsLogo: IImageProps = {
  src: `${Config.getImageURL()}/logo.png`,
  imageFit: IPImageFit.contain,
  width: 400,
  height: 200,
};

interface IAppContextProps {
  children?: ReactNode;
}

class AppContextProvider extends React.Component<IAppContextProps, IAppContext> {
  //Microsoft Entra ID through MSAL: the provider every build has, and the one Microsoft Graph tokens come from
  private readonly msalProvider: MsalAuthProvider;

  //Google Workspace through OpenID Connect, on the stages that have a Google client (Config)
  private readonly googleProvider: OidcAuthProvider | undefined;

  private readonly oidcProvider: OidcAuthProvider | undefined;

  //tail of the serialised chain of data cache refreshes triggered by a customer scope change (queueScopeRefresh)
  private customerScopeRefresh: Promise<void> = Promise.resolve();

  constructor(props: IAppContextProps) {
    super(props);

    this.state = {
      useDarkMode: false,
      setUseDarkMode: this.setUseDarkMode,
      isContentLoading: false,
      showContentLoader: this.showContentLoader,
      hideContentLoader: this.hideContentLoader,
      unhandledError: undefined,
      error: undefined,
      setError: this.setErrorMessage,
      showNotification: this.showNotification,
      hasScopes: this.hasScopes,
      getAccessToken: this.getAccessToken,
      setUserProfile: this.setUserProfile,
      hasMicrosoftIdentity: this.hasMicrosoftIdentity,
      loginWithProvider: this.loginWithProvider,
      isAuthenticated: false,
      isAuthInProgress: true, // set to true because we start with a silent SSO request
      user: CurrentUser.getEmptyUser(),
      showResourcePanel: false,
      toggleResourcePanel: this.toggleResourcePanel,
      isMainNavCollapsed: false,
      toggleMainNavCollapsed: this.toggleIsMainNavCollapsed,
      globalDataCache: new GlobalDataCache(),
      cacheMiss: this.cacheMiss,
      getGraphInterface: this.getGraphInterface,
      windowSize: globalDesktopSize,
      isMobileView: false,
      toggleSideBarPanel: this.toggleSideBarPanel,
      isOpenSideBarPanel: false,
      refreshTenantList: this.refreshTenantList,
      windowHeight: globalDesktopHeight,
      setShowResourcePanel: this.setShowResourcePanel,
      isOpenHelpPanel: false,
      setHelpPanel: this.setHelpPanel,
      adminConsentScope: this.adminConsentScope,
      startQuery: undefined,
      clearStartQuery: this.clearStartQuery,
      changeUserLanguage: this.changeUserLanguage,
      setLogo: this.setLogo,
      login: this.login,
      logout: this.logout,
      switchOrg: this.switchOrg,
      switchOrgUnit: this.switchOrgUnit,
      switchCustomer: this.switchCustomer,
      clearCustomer: this.clearCustomer,
      canScopeToCustomer: this.canScopeToCustomer,
      isCustomerScopeActive: this.isCustomerScopeActive,
      globalFilter: [],
      setGlobalFilter: this.setGlobalFilter,
      isSelectedScopeEnabled: this.isSelectedScopeEnabled,
      copilotBridge: new CopilotBridge(this.handleCopilotBridgeStateChange),
      isOnboardingInProgress: undefined,
      setOnboardingInProgress: this.setIsOnboardingInProgress,
    };

    this.msalProvider = new MsalAuthProvider(new PublicClientApplication(msalConfigCommon));
    this.googleProvider = isGoogleLoginEnabled() ? new OidcAuthProvider(IdentityProviderType.Google) : undefined;
    this.oidcProvider = isOidcLoginEnabled() ? new OidcAuthProvider(IdentityProviderType.Oidc) : undefined;
  }

  //
  // Identity providers
  //

  //the provider for a type, when this build can sign in with it; Microsoft Entra ID always can
  private getAuthProvider = (type: IdentityProviderType): IAuthProvider | undefined => {
    switch (type) {
      case IdentityProviderType.Entra:
        return this.msalProvider;
      case IdentityProviderType.Google:
        return this.googleProvider;
      case IdentityProviderType.Oidc:
        return this.oidcProvider;
      default:
        return undefined;
    }
  };

  //which provider starts the session on a cold load: a ?tid= deep link names an Entra directory, a ?idp= deep
  //link names its provider, otherwise the provider of the last completed sign-in. Without any of those on a
  //stage with more than one provider nobody can be chosen for the person: Microsoft is tried silently and the
  //welcome screen asks when there is no Microsoft session, instead of a popup that presumes Microsoft. A stage
  //with Microsoft only starts as it always did
  private resolveStartupProvider = (startQuery: string | undefined): { provider: IAuthProvider; allowInteraction: boolean } => {
    try {
      const params = new URLSearchParams(startQuery);

      if (isValidGuid(params.get('tid')?.trim() || undefined)) {
        return { provider: this.msalProvider, allowInteraction: true };
      }

      const idp = Number.parseInt(params.get('idp')?.trim() ?? '');
      if (!Number.isNaN(idp)) {
        const provider = this.getAuthProvider(idp as IdentityProviderType);
        if (provider) {
          return { provider: provider, allowInteraction: true };
        }
      }
    } catch {
      //fall through to the hint
    }

    const remembered = this.getLastIdentityProvider();
    if (remembered !== undefined) {
      return { provider: this.getAuthProvider(remembered) ?? this.msalProvider, allowInteraction: true };
    }

    return { provider: this.msalProvider, allowInteraction: !this.hasMultipleProviders };
  };

  private get hasMultipleProviders(): boolean {
    return this.googleProvider !== undefined || this.oidcProvider !== undefined;
  }

  //the provider of the signed-in user; before a sign-in the one of the last completed sign-in, else Entra
  private get activeProvider(): IAuthProvider {
    const type = this.state.isAuthenticated
      ? this.state.user.login.identityProviderType
      : (this.getLastIdentityProvider() ?? IdentityProviderType.Entra);

    return this.getAuthProvider(type) ?? this.msalProvider;
  }

  //the provider of the last completed sign-in in this browser; undefined when there was none (or a sign-out)
  private getLastIdentityProvider = (): IdentityProviderType | undefined => {
    const stored = getLocalStorageData(undefined, LocalStorageKeys.LastIdentityProvider);
    const parsed = stored !== undefined ? Number.parseInt(stored) : Number.NaN;

    return Number.isNaN(parsed) ? undefined : (parsed as IdentityProviderType);
  };

  hasMicrosoftIdentity = (): boolean => {
    return this.state.user.login.identityProviderType === IdentityProviderType.Entra;
  };

  //
  // Startup
  //
  componentDidMount() {
    try {
      //Set initial query. The landing of a Google or organization sign-in (a code and a state on the app root,
      //marked by the provider that started it) is not a start query: it is consumed by the provider, and the
      //deep link the sign-in started from, which the provider kept across its redirects, is what applies.
      let startQuery: string | undefined;
      if (OidcAuthProvider.isCallbackLanding()) {
        //a start query of an earlier load is spent
        removeSessionStorageData(SessionStorageKeys.StartQuery);
        startQuery = OidcAuthProvider.pendingReturnQuery();
        if (startQuery) {
          this.setState({ startQuery: startQuery });
        }
      } else if (window.location.search) {
        startQuery = window.location.search;
        //save the query params to the session state to apply again after auth redirect
        setSessionStorageData(SessionStorageKeys.StartQuery, startQuery);
        this.setState({ startQuery: startQuery });
      } else {
        const query = getSessionStorageData(SessionStorageKeys.StartQuery);
        if (query) {
          //apply any previously saved start query and remove it
          removeSessionStorageData(SessionStorageKeys.StartQuery);
          this.setState({ startQuery: query });
          startQuery = query;
        }
      }

      // Set the light or dark mode
      const useDarkMode = getLocalStorageData(this.state, LocalStorageKeys.DarkMode) === 'true';
      this.setState({
        useDarkMode: useDarkMode,
      });
      // Sync theme-dependent CSS variables (e.g. --app-error-text) on initial load so
      // globalTextStylesError renders with the dark-mode-friendly red from first paint.
      applyThemeCssVars(useDarkMode);

      // Set show resource panel
      const showResourcePanel = getLocalStorageData(this.state, LocalStorageKeys.ShowResourcePanel);
      this.setState({
        showResourcePanel: !showResourcePanel || showResourcePanel === 'true',
      });

      // Set main menu collapsed
      const mainNavCollapsed = getLocalStorageData(this.state, LocalStorageKeys.MainNavCollapsed);
      this.setState({
        isMainNavCollapsed: mainNavCollapsed === 'true',
      });

      // Add main window resize event
      window.addEventListener('resize', this.onResize);
      this.onResize();

      // Init Supademo
      initSupademo('86b251eee52fccab56e7cd47c0dba28bf90e53c2d551fcdde4f4949e4c0397c1', {
        variables: {
          email: '',
          name: '',
          custom_variable: '',
        },
      }).catch((err) => console.debug('Supademo initialization failed', err));

      //start the authentication process
      this.initializeAuth(startQuery);
    } catch (err) {
      this.setErrorMessage(err);
    }
  }

  private handleCopilotBridgeStateChange = (state: CopilotBridgeState, changed: Partial<CopilotBridgeState>) => {
    this.setState({ copilotBridge: this.state.copilotBridge });
  };

  initializeAuth = async (startQuery: string | undefined) => {
    // Initialize every provider's client (the MSAL application object, the stored Google session), so that the
    // cold-load restore below can ask any of them for its active directory
    await Promise.all([this.msalProvider.initialize(), this.googleProvider?.initialize(), this.oidcProvider?.initialize()]);

    // The landing of a Google or organization sign-in completes that sign-in before anything else. The provider
    // that started it marked itself before the redirect; the return url it kept sets the org unit first, so the
    // login request carries it. No default: a code without a marker of ours is not a landing (an Entra admin
    // consent decline carries state and error too).
    if (OidcAuthProvider.isCallbackLanding()) {
      const pendingType = OidcAuthProvider.pendingProviderType();
      const pendingProvider =
        pendingType === IdentityProviderType.Oidc
          ? this.oidcProvider
          : pendingType === IdentityProviderType.Google
            ? this.googleProvider
            : undefined;

      if (pendingProvider) {
        this.getTenantFromRoute(startQuery);
        await pendingProvider.handleRedirectResponse(this.authStateUpdate, this.setGlobalDataCache);

        return;
      }

      logger.debug(`No provider ${pendingType} in this build for the sign-in that is landing`);
    }

    //a load that is not a landing ends whatever sign-in was in flight (Back at the issuer): no stale marker or return url
    OidcAuthProvider.clearPendingSignIn();

    //no sign-in starts on its own on the trial start page, on the signed-out page or on the cloud setup page (opened by
    //a cloud administrator without an ISOPlanner account); a Microsoft redirect that is landing there still completes
    //(MSAL is the only provider that lands outside the root)
    const pathname = window.location.pathname;
    if (pathname === '/start' || pathname === '/logout' || pathname === globalCloudSetupURL) {
      const redirectHandled = await this.msalProvider.handleRedirectResponse(this.authStateUpdate, this.setGlobalDataCache);
      if (redirectHandled) {
        return;
      }

      logger.debug(`No automatic sign-in on ${pathname}`);

      //render the welcome screen (or the signed-out page) and do not automatically try to login
      const newState = {
        isAuthenticated: false,
        isAuthInProgress: false,
        error: undefined,
      };

      this.setState(newState);

      return;
    }

    //restore a previously selected org unit that is linked to its own directory (separate IdP) so the
    //orgunitid header is present on this cold load. A URL deep-link (?ouid=/?tid=) still takes precedence below.
    this.restoreSeparateDirectoryOrgUnit();

    // Try to login silently with the provider the deep link or the last sign-in names
    const startup = this.resolveStartupProvider(startQuery);
    await startup.provider.ssoSilent(
      this.getTenantFromRoute(startQuery),
      this.authStateUpdate,
      this.setGlobalDataCache,
      startup.allowInteraction,
    );
  };

  //
  // Restore the org unit header on a cold load for an org unit that lives in its own directory.
  // Safe by construction: the header is only restored when the active account of the org unit's identity provider
  // belongs to the org unit's directory, so the back-end will accept the login. If it does not match (e.g. the active
  // account reverted to the home directory), nothing is restored and login proceeds exactly as before.
  //
  restoreSeparateDirectoryOrgUnit = () => {
    try {
      if (globalOrgUnitId) return; //already set (e.g. via a deep-link), do not override

      const stored = getLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit);
      if (!stored) return;

      const hint = JSON.parse(stored) as SeparateDirectoryOrgUnitHint;
      if (!isValidGuid(hint.orgUnitId) || !hint.providerTenantId) return;

      const activeDirectoryId = this.getAuthProvider(hint.identityProviderType)?.getActiveDirectoryId();
      if (activeDirectoryId?.toLowerCase() === hint.providerTenantId.toLowerCase()) {
        this.setOrgUnit(hint.orgUnitId);
        logger.debug(
          `Restored separate-directory org unit ${hint.orgUnitId} for directory ${hint.providerTenantId} on cold load`,
        );
      } else {
        logger.debug(
          `Not restoring separate-directory org unit ${hint.orgUnitId}: active directory '${activeDirectoryId}' does not match '${hint.providerTenantId}'`,
        );
      }
    } catch {
      //no-op: never block login on restore
    }
  };

  componentWillUnmount() {
    //remove resize event
    window.removeEventListener('resize', this.onResize);
  }

  clearStartQuery = () => {
    if (this.state.startQuery) {
      this.setState({ startQuery: undefined });
    }
  };

  getTenantFromRoute = (startQuery: string | undefined): string | undefined => {
    try {
      logger.debug('Start query: ' + startQuery);
      const params = new URLSearchParams(startQuery);

      //both ids travel on as the org unit header and the tid as an MSAL authority: a hand-typed link may carry
      //braces or capitals, which the API's text compare of the top-level marker and MSAL do not take
      let urlOrgUnitParam = params.get('ouid')?.trim() || undefined;
      if (urlOrgUnitParam && isValidGuid(urlOrgUnitParam)) {
        urlOrgUnitParam = normalizeGuid(urlOrgUnitParam);
        this.setOrgUnit(urlOrgUnitParam);
        this.clearStartQuery();
      } else {
        urlOrgUnitParam = undefined;
      }

      let urlTenantParam = params.get('tid')?.trim() || undefined;
      if (urlTenantParam && isValidGuid(urlTenantParam)) {
        urlTenantParam = normalizeGuid(urlTenantParam);

        //the tid must be the Azure Tenant Id
        //When a specific tenant is requested but no OU is specified, it is expected that the user will be logged into the top level tenant.
        //However, the API will automatically log into the OU that was last accessed by the user, unless an OU is specified
        //We can set the OU to the same Id as the top level tenant to signal the API to log into the top level tenant
        if (!urlOrgUnitParam) {
          this.setOrgUnit(urlTenantParam);
          this.clearStartQuery();
        }

        return urlTenantParam;
      }

      return undefined;
    } catch {
      return undefined;
    }
  };

  //
  // Loader functions
  //
  showContentLoader = () => this.setState({ isContentLoading: true });

  hideContentLoader = () => this.setState({ isContentLoading: false });

  //
  // Light and dark mode functions
  //
  setUseDarkMode = (useDarkMode: boolean) => {
    setLocalStorageData(this.state, LocalStorageKeys.DarkMode, useDarkMode.toString(), false);
    this.setState({ useDarkMode: useDarkMode });
    applyThemeCssVars(useDarkMode);
  };

  //
  // Preview features
  //
  enablePreviewFeatures = (type: SubscriptionTypes) => {
    //enable preview languages
  };

  //
  // Onboarding state
  //
  setIsOnboardingInProgress = (inProgress: boolean) => {
    this.setState({ isOnboardingInProgress: inProgress });
  };

  //
  // Authentication service wrapper functions
  //
  authStateUpdate = (
    isAuthenticated?: boolean | undefined,
    user?: CurrentUser | undefined,
    isAuthInProgress?: boolean | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any | undefined,
  ) => {
    //when authentication is done and we have a user
    if (user && !isAuthInProgress) {
      //enable preview features
      if (user.login.subscriptionType) {
        this.enablePreviewFeatures(user.login.subscriptionType);
      }

      //apply the user language
      logger.debug('Setting user language to: ' + (user.login.userLanguageCode ?? user.language.code));
      if (user.login.userLanguageCode) {
        // Language setting from database overrides the Microsoft account setting
        user.language = new UserLanguage(user.login.userLanguageCode);
      }
      i18nBase.changeLanguage(user.language.code);

      // set language headers
      this.setLanguages(user);

      // let the global data cache know so it can change the language
      this.state.globalDataCache.setCurrentUserLanguage(user);

      //update the global state for the organizational unit when user has logged into one
      if (user.login.isOrgUnit) {
        this.setOrgUnit(user.login.tenantId);

        //persist an org unit that is linked to its own directory, so a cold page reload can re-send the
        //orgunitid header. Without it the foreign token (directory != top level) cannot resolve a tenant and login
        //fails. We store the org unit's directory alongside it so the restore can verify the active account matches.
        //Never persist a backup checkpoint preview: a cold load must not land inside the preview again.
        if (user.login.orgUnitHasSeparateIdentityProvider && !user.login.isCheckpointPreview) {
          const orgUnit = user.login.tenants.find((tn) => tn.id === user.login.tenantId);
          if (orgUnit?.providerTenantId) {
            const hint: SeparateDirectoryOrgUnitHint = {
              orgUnitId: user.login.tenantId,
              identityProviderType: orgUnit.identityProviderType,
              providerTenantId: orgUnit.providerTenantId,
            };
            setLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit, JSON.stringify(hint), false);
          }
        } else {
          removeLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit);
        }
      } else {
        this.setOrgUnit(undefined);
        removeLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit);
      }

      //remember the identity provider of a completed sign-in, so the next cold load starts with the same one
      if (isAuthenticated === true) {
        setLocalStorageData(
          undefined,
          LocalStorageKeys.LastIdentityProvider,
          user.login.identityProviderType.toString(),
          false,
        );
      }

      //Always drop the customer scope on a completed sign-in or profile reload. Unlike the org unit there is no
      //signal to reconcile against - the back-end returns nothing that says "this session is scoped to a customer"
      //(loginDTO.isPartner describes the resolved tenant) - and a fresh profile always means partner context again.
      //The scope is deliberately not persisted to localStorage either: a reload is the safety valve that drops it.
      this.setCustomer(undefined);

      // Update Supademo with user info
      updateSupademoUser({
        email: user.email,
        name: user.name,
        custom_variable: user.userLanguageCode,
      });

      // Update the state with 1 statement, otherwise timing issues will arrise
      // So for each state property to set, check if it's defined and if so, set it, otherwise set it to the current state
      const newState = {
        isAuthenticated: isAuthenticated !== undefined ? isAuthenticated : this.state.isAuthenticated,
        isAuthInProgress: isAuthInProgress !== undefined ? isAuthInProgress : this.state.isAuthInProgress,
        user: user,
        error: error !== undefined ? error : this.state.error,
        isOnboardingInProgress:
          this.state.isOnboardingInProgress !== undefined
            ? this.state.isOnboardingInProgress
            : user.login.isOnboardingInProgress,
      };

      this.setState(newState, () => {
        //the photos come from Microsoft Graph and the cache asks the signed-in user whether they have a Microsoft
        //identity: kicked off once that user is in state, not when the cache arrived (that was one step earlier)
        if (isAuthenticated === true && (this.state.globalDataCache.settings.get(AzureADPhotos) as boolean)) {
          this.state.globalDataCache.users.cachePhotos(); //async cache photos
        }
      });
    } else if (isAuthInProgress) {
      // Only set isAuthInProgress to true, keep the user and isAuthenticated state until we have a result
      const newState = {
        isAuthenticated: isAuthenticated !== undefined ? isAuthenticated : this.state.isAuthenticated,
        isAuthInProgress: true,
        user: user !== undefined ? user : this.state.user,
        error: error !== undefined ? error : this.state.error,
      };

      this.setState(newState);
    }
  };

  //the user menu's "sign in with another account": the provider of the signed-in person shows its chooser. The
  //sign-in buttons of the welcome screen name their provider through loginWithProvider instead
  login = async () => {
    return await this.loginWithProvider(this.activeProvider.providerType, undefined, true);
  };

  loginWithProvider = async (type: IdentityProviderType, directoryId?: string, switchAccount: boolean = false) => {
    const provider = this.getAuthProvider(type);
    if (!provider) {
      logger.debug(`Identity provider ${type} is not available in this build`);

      return;
    }

    resetHubSpotChat();
    this.setOrgUnit(undefined);
    this.setCustomer(undefined);

    return await provider.login(directoryId, this.authStateUpdate, this.setGlobalDataCache, switchAccount);
  };

  logout = async () => {
    resetHubSpotChat();
    this.setOrgUnit(undefined);
    this.setCustomer(undefined);
    //the provider is read before the hint goes, so the sign-out reaches the provider the user signed in with
    const provider = this.activeProvider;
    removeLocalStorageData(undefined, LocalStorageKeys.LastIdentityProvider);
    //the org unit hint is provider-neutral, and the next sign-in in this browser may be someone else's
    removeLocalStorageData(undefined, LocalStorageKeys.LastSeparateDirectoryOrgUnit);

    return await provider.logout(this.authStateUpdate);
  };

  switchOrg = async (item: Tenant) => {
    //the organization decides which identity provider signs the user in to it; one this build cannot sign in
    //with is said out loud, the user clicked and would otherwise see nothing happen
    const provider = this.getAuthProvider(item.identityProviderType);
    if (!provider) {
      logger.debug(`Identity provider ${item.identityProviderType} of tenant ${item.id} is not available in this build`);
      //the namespace is loaded by the components that use it, not by this class
      await i18nBase.loadNamespaces('identityproviders');
      this.showNotification(
        i18nBase.t('identityproviders:Errors.ProviderNotAvailable', {
          tenant: item.name,
          provider: getIdentityProviderName(item.identityProviderType),
        }),
        true,
      );

      return;
    }

    resetHubSpotChat();
    this.setCustomer(undefined);
    if (item.parentId) {
      //request for a direct switch to an org unit within a guest
      this.setOrgUnit(item.id);
    } else {
      //set the org unit to the same Id as the Azure tenant Id
      //so that the back-end knows to login into the top level tenant instead of the last known org unit
      this.setOrgUnit(item.id);
    }

    //the next login decides for the new tenant whether its set-up still hides the main navigation
    this.setState({ isOnboardingInProgress: undefined });
    await provider.switchTenant(item, this.authStateUpdate, this.setGlobalDataCache);
  };

  switchOrgUnit = async (item: Tenant) => {
    resetHubSpotChat();
    this.setCustomer(undefined);
    this.setOrgUnit(item.id);
    //the next login decides for the new org unit whether its set-up still hides the main navigation
    this.setState({ isOnboardingInProgress: undefined });
    await this.activeProvider.setUserProfile(this.authStateUpdate, this.setGlobalDataCache);
  };

  setOrgUnit = (orgUnitId: string | undefined) => {
    if (orgUnitId) {
      setGlobalOrgUnitId(orgUnitId);
    } else {
      setGlobalOrgUnitId(undefined);
    }
  };

  //The customer scope may not be combined with an org unit: the back-end gives orgunitid priority and never reads
  //customerid when both travel on the same request, so the dashboard would silently render the wrong tenant.
  //Callers cannot derive this themselves - user.login.isOrgUnit misses the ?tid=/?ouid= deep-link case, which sets
  //the org unit global on a top level login.
  canScopeToCustomer = (): boolean => {
    return globalOrgUnitId === undefined;
  };

  //Whether a customer scope is active right now. Distinct from canScopeToCustomer, which asks whether one could
  //be started. Used to hold back the things that must not run against a customer - anything Graph backed, which
  //still addresses the partner's own directory whatever the scope says.
  isCustomerScopeActive = (): boolean => {
    return globalCustomerId !== undefined;
  };

  //Activate the customer scope. Sets the global synchronously before any await so that a component unmounting
  //mid-load always finds a scope to clear; the caller must therefore call this before its own data fetches.
  switchCustomer = async (customerTenantId: string): Promise<boolean> => {
    if (!isValidGuid(customerTenantId) || !this.canScopeToCustomer()) {
      return false;
    }

    this.setCustomer(customerTenantId);
    await this.queueScopeRefresh();

    return true;
  };

  clearCustomer = async () => {
    if (globalCustomerId === undefined) {
      return;
    }
    this.setCustomer(undefined);
    await this.queueScopeRefresh();
  };

  //Serialise the cache refreshes that follow a scope change. Both switchCustomer and clearCustomer rewrite the one
  //shared globalDataCache instance, so two overlapping runs interleave per cache and the partner can be left
  //looking at the customer's users, tags and lists on ordinary screens. Chaining makes the run queued last the run
  //that writes last, which is exactly the order the scope changed in.
  //A generation counter would not work here: refresh() writes straight into the shared caches, so there is no
  //result for the caller to discard.
  queueScopeRefresh = (): Promise<void> => {
    this.customerScopeRefresh = this.customerScopeRefresh
      .catch(() => {}) //a failed refresh must not break the chain for the next scope change
      .then(() => this.state.globalDataCache.refresh());

    return this.customerScopeRefresh;
  };

  setCustomer = (customerId: string | undefined) => {
    if (customerId) {
      setGlobalCustomerId(customerId);
    } else {
      setGlobalCustomerId(undefined);
    }
  };

  changeUserLanguage = async (code: string): Promise<string> => {
    const newUser = this.state.user;

    if (!UserLanguage.getSupportedLanguages().has(code)) {
      //revert to the fallback language
      newUser.language = new UserLanguage(UserLanguage.getFallBack());
    } else {
      newUser.language = new UserLanguage(code);
    }

    //inform the localization service to change the language
    await i18nBase.changeLanguage(newUser.language.codeWithCulture);

    //re-initialize the global data cache with the new language
    this.state.globalDataCache.setCurrentUserLanguage(newUser);

    //re-initialize the global language variables for sending in headers
    this.setLanguages(newUser);

    //update the global state
    this.setState({ user: newUser });

    return newUser.language.code;
  };

  setLanguages = (user: CurrentUser) => {
    setGlobalUserLang(user.language.code);
    setGlobalDefOrgLang(this.state.globalDataCache.settings.get(DefLanguageCode) as string);
  };

  adminConsentScope = (clientId: string, scopes: string[], redirectUrl: string, stateSuffix?: string) => {
    const scopeStr = scopes.join(' ');

    return this.activeProvider.adminConsentScope(clientId, scopeStr, this.authStateUpdate, redirectUrl, stateSuffix);
  };

  hasScopes = async (scopes: string[]) => {
    return await this.activeProvider.hasScopes(scopes);
  };

  getAccessToken = async (scopes: string[]) => {
    return await this.activeProvider.getAccessToken(scopes);
  };

  setUserProfile = async () => {
    //called right after a trial was started: the login of the new tenant decides whether its set-up hides the
    //main navigation, so the value kept from the login before the trial existed must not stick
    this.setState({ isOnboardingInProgress: undefined });

    return await this.activeProvider.setUserProfile(this.authStateUpdate, this.setGlobalDataCache);
  };

  getGraphInterface = async (
    scopes: string[] | GraphScopeId,
    tenantId?: string,
    allowInteraction: boolean = true,
  ): Promise<IGraphInterface> => {
    //Get a graph client interface for the requested scopes and tenant.

    //Graph is a Microsoft 365 feature: a user of another identity provider has no token for it, and asking
    //MSAL for one would open a Microsoft sign-in popup for them
    if (!this.hasMicrosoftIdentity()) {
      throw new AppError('The signed-in user has no Microsoft identity', AuthErrorCodes.MicrosoftIdentityRequired);
    }

    //When a tenantId is supplied, check if the current user has a valid license and try to get an access token for that tenant.
    //This may result in that the user must login to that tenant
    let tenant: Tenant | undefined = undefined;
    if (tenantId) {
      tenant = this.state.user.login.tenants?.find((t) => t.providerTenantId === tenantId);
      if (!tenant) {
        logger.debug(`Requested Graph interface for specific but tenant was not found: ${tenantId}`);
      }
    }

    if (typeof scopes === 'number') {
      //When the scopes are a GraphScopeId, convert to the actual scopes
      //Return the correct scopes based on the setting to request selected scopes
      if (this.isSelectedScopeEnabled(appClientId)) {
        scopes = graphSharepointSelectedRequest.scopes;
      } else {
        switch (scopes) {
          case GraphScopeId.Pages:
            scopes = graphSharepointPagesRequest.scopes;
            break;
          case GraphScopeId.Library:
            scopes = graphSharepointLibraryRequest.scopes;
            break;
          case GraphScopeId.SiteOwner:
            scopes = graphSharepointManageRequest.scopes;
            break;
          default:
            throw new AppError(`Invalid GraphScopeId: ${scopes}.`);
        }
      }
    }

    //Get the access token for the requested scopes and tenant
    const accessToken = await this.msalProvider.getAccessToken(scopes, tenant, allowInteraction);

    //get the Graph client interface
    const client = AuthService.getGraphClient(accessToken);
    const graph: IGraphInterface = { accessToken, client };

    return graph;
  };

  isSelectedScopeEnabled = (clientId: string): boolean => {
    switch (clientId) {
      case apiClientId:
        const apiSetting = this.state.globalDataCache.settings.get(APISelectedSites) as SelectedSitesSteps;
        if (apiSetting && apiSetting.status[SelectedSitesStep.Enabled] === true) {
          return true;
        }
        break;
      case appClientId:
        const appSetting = this.state.globalDataCache.settings.get(APPSelectedSites) as SelectedSitesSteps;
        if (appSetting && appSetting.status[SelectedSitesStep.Enabled] === true) {
          return true;
        }
        break;
      case svcClientId:
        const svcSetting = this.state.globalDataCache.settings.get(SVCSelectedSites) as SelectedSitesSteps;
        if (svcSetting && svcSetting.status[SelectedSitesStep.Enabled] === true) {
          return true;
        }
        break;
      default:
        break;
    }

    return false;
  };

  refreshTenantList = async () => {
    try {
      const newUser = this.state.user.clone();
      const accessToken = await this.activeProvider.getAccessToken(apiRequest.scopes);
      const newOrgUnitsList = await apiGetTenantsForUser(accessToken);
      newUser.login.tenants = newOrgUnitsList;
      this.setState({ user: newUser });
    } catch (err) {
      this.setErrorMessage(err);
    }
  };

  //
  // Global cache functions
  //
  setLogo = (logo: Blob | undefined) => {
    const newUser = this.state.user.clone();
    newUser.tenant.appLogo = logo;
    this.setState({ user: newUser });
  };

  setGlobalDataCache = (globalDataCache: GlobalDataCache) => {
    //initialize the global data cache retrieved from the back-end at login
    //1. apply the IAppContext so that the cache can use API calls
    globalDataCache.setAppContext(this.state);
    //2. clone the cache so that we can use it in the state
    const newGlobalDataCache = globalDataCache.clone();
    //3. the background processes that retrieve more information (the photos) start from authStateUpdate, once
    //   the signed-in user is in state as well
    this.setState({ globalDataCache: newGlobalDataCache }); //set in state
  };

  cacheMiss = async (count: number) => {
    //disable cache refresh. this is not possible in a React rendering phase.
  };

  //
  // Global error handling
  //
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    let newUnhandledError = this.getAppError(error);

    if (!newUnhandledError) {
      newUnhandledError = new AppError('Unknown error');
    }

    //try to refresh the browser for certain errors
    let resolveByReload = false;

    //1. When Babel cannot load a module: this can be the case after a live update
    resolveByReload =
      newUnhandledError.message?.includes('Loading chunk') && newUnhandledError.message?.includes('failed');

    //reload or show error
    if (resolveByReload) {
      window.location.reload();
    } else {
      newUnhandledError.code = '999';
      newUnhandledError.debug = errorInfo.componentStack || '';

      this.setState({
        unhandledError: newUnhandledError,
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setErrorMessage = (error: string | any) => {
    const appError = this.getAppError(error);
    if (isAbortOrCancelError(appError)) {
      return;
    }

    this.setState({
      error: appError,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAppError = (error: string | any): AppError | undefined => {
    const appError = normalizeError(error);

    //when this instance is the same as the instance in the state, return the state
    if (appError?.isEqual(this.state.error)) {
      return this.state.error;
    } else {
      return appError;
    }
  };

  showNotification = (
    msg: string | undefined,
    isError: boolean = false,
    isWarning: boolean = false,
    isBottom: boolean = false,
    link?: { text: string; onClick: () => void },
  ) => {
    if (!msg) {
      msg = 'Unknown error, please contact support';
    }

    const containerId = isBottom ? 'app-bottom' : 'app';
    const content = link ? (
      <span>
        {msg} <IPLink onClick={link.onClick}>{link.text}</IPLink>
      </span>
    ) : (
      msg
    );

    if (isError) {
      toast.error(content, { containerId: containerId });
    } else if (isWarning) {
      toast.warn(content, { containerId: containerId });
    } else {
      toast.success(content, { containerId: containerId });
    }
  };

  //
  // Panel functions
  //
  toggleResourcePanel = () => {
    this.setState({ showResourcePanel: !this.state.showResourcePanel });
  };

  setShowResourcePanel = (enable: boolean) => {
    this.setState({ showResourcePanel: enable });
  };

  toggleIsMainNavCollapsed = () => {
    this.setState({ isMainNavCollapsed: !this.state.isMainNavCollapsed });
  };

  toggleSideBarPanel = () => {
    this.setState({ isOpenSideBarPanel: !this.state.isOpenSideBarPanel });
  };

  setHelpPanel = (enable: boolean) => {
    this.setState({ isOpenHelpPanel: enable });
  };

  //
  // Mobile functions
  //
  getWindowWidth = (): number => {
    return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
  };

  getWindowHeight = (): number => {
    return Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
  };

  onResize = () => {
    try {
      const width = this.getWindowWidth();
      const height = this.getWindowHeight();
      this.setState({
        windowSize: width,
        windowHeight: height,
        isMobileView: width >= globalDesktopSize ? false : true,
      });
    } catch {
      //ignore
    }
  };

  //
  // Global filter functions
  //
  setGlobalFilter = (filter: string[]) => {
    this.setState({ globalFilter: filter });
  };

  //
  // Main render
  //
  render() {
    if (this.state.unhandledError) {
      return (
        <IPStack horizontalAlign="center">
          <ErrorMessage error={this.state.unhandledError} />
          <IPImage {...imagePropsLogo} />
        </IPStack>
      );
    } else {
      return <AppContext.Provider value={this.state}>{this.props.children}</AppContext.Provider>;
    }
  }
}

export default AppContextProvider;
